import {
  Horuz,
  HoruzProtocolChulakClientController,
  HoruzProtocolChulakClientStateEvent,
  HoruzProtocolChulakClientStateInvoker,
  HoruzProtocolChulakClientStateYielder,
  HoruzProtocolChulakCloseCode,
  HoruzProtocolChulakErrorAgentCancel,
  HoruzProtocolChulakErrorAgentDisconnect,
  HoruzProtocolChulakServiceSessionGuardTags,
  HoruzProtocolChulakServiceSessionGuardTagsType,
  IHoruz,
} from '@ver.id/libs.internal.horuz';
import { toCompact, CompactJWS } from '../utils/compact';

// Horuz singleton
const horuz = new Horuz();

// The client state machine from statemap.ts as Ronan shows it: the protocol's
// AppPresentation (the wallet's reply) goes straight to Done, with no consent
// screen in between. The reply is a bare signal: the shared data never reaches
// the browser, it goes to the relying party through the backend.
export enum ChulakState {
  Init = 'Init',
  Auth = 'Auth',
  AgentStart = 'AgentStart',
  AppPairing = 'AppPairing',
  WaitingForPresentation = 'WaitingForPresentation',
  Done = 'Done',
  Abort = 'Abort',
}

export type ChulakContext =
  | { state: ChulakState.Init }
  | { state: ChulakState.Auth }
  | {
      state: ChulakState.AgentStart;
      tags: HoruzProtocolChulakServiceSessionGuardTags;
    }
  | {
      state: ChulakState.AppPairing;
      tags: HoruzProtocolChulakServiceSessionGuardTags;
      pairingCode: string;
    }
  | { state: ChulakState.WaitingForPresentation }
  | {
      state: ChulakState.Done;
      continueInApp: boolean;
    }
  | { state: ChulakState.Abort; errorCode?: number; errorMessage?: string };

/**
 * Detects transient WebSocket disconnects (e.g. mobile tab suspension).
 * These are safe to auto-retry because the server has session recovery.
 */
export function isTransientDisconnect(ctx: ChulakContext): boolean {
  if (ctx.state !== ChulakState.Abort) return false;
  // WS close code 1006 (abnormal closure) hits Horuz's default handler,
  // which produces PROTOCOL_UNKNOWN_ERROR with "1006" in the message.
  return (
    ctx.errorCode === HoruzProtocolChulakCloseCode.PROTOCOL_UNKNOWN_ERROR &&
    !!ctx.errorMessage?.includes('1006')
  );
}

export type ChulakActions = {
  cleanup: () => void;
  abort: () => void;
  completePairing: () => void;
};

export type ChulakSetupResult = {
  clientContextIdentifier: string;
  client: HoruzProtocolChulakClientController<IHoruz>;
};

/**
 * Create WebSocket connection to Chulak server
 */
export const setupChulak = (url: string): Promise<ChulakSetupResult> => {
  return new Promise((resolve, reject) => {
    const wsc = new WebSocket(url);
    wsc.binaryType = 'arraybuffer';

    wsc.onopen = () => {
      const client = horuz.$('protocols').$('chulak').$('client');
      const clientContextIdentifier = client.handle(wsc);
      resolve({ clientContextIdentifier, client });
    };

    wsc.onclose = () => {
      reject(new Error('WebSocket closed before open'));
    };
  });
};

/**
 * Connect to Chulak and wire up all state invokers/events.
 * Returns a cleanup function.
 */
export type ConnectChulakOptions = {
  fresh?: boolean;
  onStateChange: (ctx: ChulakContext) => void;
};

export const connectChulak = (
  handlerUrl: string,
  requestToken: CompactJWS,
  options: ConnectChulakOptions,
): ChulakActions => {
  const { fresh, onStateChange } = options;
  let aborted = false;
  let clientRef: HoruzProtocolChulakClientController<IHoruz> | null = null;
  let contextIdRef: string | null = null;
  let currentTags: HoruzProtocolChulakServiceSessionGuardTags | null = null;
  let currentState: ChulakState = ChulakState.Init;

  const stateOrder: Record<ChulakState, number> = {
    [ChulakState.Init]: 0,
    [ChulakState.Auth]: 1,
    [ChulakState.AgentStart]: 2,
    [ChulakState.AppPairing]: 3,
    [ChulakState.WaitingForPresentation]: 4,
    [ChulakState.Done]: 5,
    [ChulakState.Abort]: 5,
  };

  const emitStateChange = (ctx: ChulakContext) => {
    currentState = ctx.state;
    onStateChange(ctx);
  };

  const wsUrl = handlerUrl
    .replace('chulak:', 'ws:')
    .replace('chulaks:', 'wss:');

  setupChulak(wsUrl)
    .then(({ client, clientContextIdentifier }) => {
      if (aborted) return;
      clientRef = client;
      contextIdRef = clientContextIdentifier;

      // Auth: send compact token
      client
        .$('Auth')
        .invoker(
          clientContextIdentifier,
          HoruzProtocolChulakClientStateInvoker.CreateAuthentication,
          async () => ({ compact: toCompact(requestToken), fresh }),
        );

      // Auth completed: only suppress AgentStart during transient reconnects
      // (fresh=false + existing lastState). On such reconnects the server will
      // fast-forward to the correct state and the client will receive the
      // appropriate STATE_*_ENTER message, so emitting AgentStart here would
      // cause a brief incorrect flash of that screen.
      // Fresh connections (explicit navigation, restart, initial load) always
      // emit AgentStart so the user isn't stuck on the Auth/loading screen.
      client
        .$('Auth')
        .invoker(
          clientContextIdentifier,
          HoruzProtocolChulakClientStateInvoker.CompletedAuthentication,
          async (_context, input) => {
            const tags =
              input.tags.type ===
              HoruzProtocolChulakServiceSessionGuardTagsType.Dynamic
                ? input.tags.dynamic.tags
                : input.tags;
            currentTags = tags;

            const isRecovery =
              !fresh &&
              _context.stateContextRegistry.localServiceSession?.lastState !=
                null;
            if (!isRecovery) {
              emitStateChange({ state: ChulakState.AgentStart, tags });
            }
          },
        );

      // AgentStart: tags refreshed
      client
        .$('AgentStart')
        .invoker(
          clientContextIdentifier,
          HoruzProtocolChulakClientStateInvoker.RefreshedAppTags,
          async (_context, input) => {
            currentTags = input.tags;
            // Only emit AgentStart if we haven't progressed past it
            if (
              stateOrder[currentState] <= stateOrder[ChulakState.AgentStart]
            ) {
              emitStateChange({
                state: ChulakState.AgentStart,
                tags: input.tags,
              });
            }
          },
        );

      // AgentStart: completed
      client
        .$('AgentStart')
        .invoker(
          clientContextIdentifier,
          HoruzProtocolChulakClientStateInvoker.CompletedAgentStart,
          async () => {
            // Agent start completed — no action needed
          },
        );

      // AppPairing: verify
      client
        .$('AppPairing')
        .invoker(
          clientContextIdentifier,
          HoruzProtocolChulakClientStateInvoker.VerifyAppPairing,
          async (_context, input) => {
            emitStateChange({
              state: ChulakState.AppPairing,
              tags: currentTags!,
              pairingCode: input.code,
            });
          },
        );

      // AppPresentation: waiting for wallet to complete
      client
        .$('AppPresentation')
        .invoker(
          clientContextIdentifier,
          HoruzProtocolChulakClientStateInvoker.WaitingForPresentation,
          async () => {
            emitStateChange({ state: ChulakState.WaitingForPresentation });
          },
        );

      // AppPresentation: completed
      client
        .$('AppPresentation')
        .invoker(
          clientContextIdentifier,
          HoruzProtocolChulakClientStateInvoker.CompletedPresentation,
          async () => {
            emitStateChange({
              state: ChulakState.Done,
              continueInApp: !!currentTags?.app?.continueInApp,
            });
          },
        );

      // Abort handler
      client
        .$('Abort')
        .on(
          clientContextIdentifier,
          HoruzProtocolChulakClientStateEvent.Aborted,
          async (_, input) => {
            emitStateChange({
              state: ChulakState.Abort,
              errorCode: input.error?.code,
              errorMessage: input.error?.message,
            });
          },
        );

      // Start the client state machine
      client.$startContext(clientContextIdentifier, {});
      emitStateChange({ state: ChulakState.Auth });
    })
    .catch((err) => {
      if (!aborted) {
        // WebSocket failures throw Event or Error with 'WebSocket closed',
        // not TypeError like fetch. Treat all setup failures as network issues
        // unless we can confirm otherwise.
        const isFetchError =
          err instanceof TypeError && err.message === 'Failed to fetch';
        const isWsError =
          err instanceof Error &&
          err.message === 'WebSocket closed before open';

        if (isFetchError || isWsError) {
          emitStateChange({
            state: ChulakState.Abort,
            errorCode: HoruzProtocolChulakCloseCode.AGENT_DISCONNECT,
            errorMessage: 'Network error',
          });
        } else {
          // The wallet's service is unreachable: shown as "app unavailable".
          emitStateChange({
            state: ChulakState.Abort,
            errorCode: HoruzProtocolChulakCloseCode.APP_ERROR,
          });
        }
      }
    });

  return {
    cleanup: () => {
      aborted = true;
      if (clientRef && contextIdRef) {
        try {
          const context = clientRef.$getContext(contextIdRef);
          context.stateContext.stateContextRegistry.lastError =
            HoruzProtocolChulakErrorAgentDisconnect.Create('Going away.');
          clientRef.$tryAbortContext(contextIdRef, { stateAbort: 'Abort' });
        } catch {
          // ignore cleanup errors
        }
      }
    },
    abort: () => {
      if (clientRef && contextIdRef) {
        try {
          const context = clientRef.$getContext(contextIdRef);
          context.stateContext.stateContextRegistry.lastError =
            HoruzProtocolChulakErrorAgentCancel.Create('User cancelled.');
          clientRef.$tryAbortContext(contextIdRef, { stateAbort: 'Abort' });
        } catch {
          // ignore errors
        }
      }
    },
    completePairing: () => {
      if (clientRef && contextIdRef) {
        clientRef
          .$('AppPairing')
          .yielder(
            contextIdRef,
            HoruzProtocolChulakClientStateYielder.CompletedAppPairing,
            {},
          );
      }
    },
  };
};
